import { FileTypeEnum } from 'api/documents';
import { Button } from 'components/ui/Button';
import { GenericLoading } from 'components/ui/Loader';
import { CheckIcon, UploadIcon, DocumentIcon, TrashIcon } from 'components/icons/Icons';
import { Sizes } from 'components/forms/NumberSelector';
import { ErrorMessage } from 'components/forms/fragments/FieldSet';
import { FC, useCallback, useEffect, useState, ReactNode, DragEvent } from 'react';
import { Eye } from 'lucide-react';
import { useDocumentViewerDialog } from './useDocumentViewerDialog';

interface GenericDocumentUploaderProps {
  documentName: string;
  fileType: FileTypeEnum;
  storedFileId?: string;
  onUpload: (file: File, fileType: FileTypeEnum) => Promise<{ success: boolean; error?: string }>;
  onRemove?: (storedFileId: string) => Promise<void>;
  disabled?: boolean;
  acceptedFormats?: string;
  className?: string;
  uploadLabel?: string;
  uploadedLabel?: string;
  renderUploadButton?: (onClick: () => void, isLoading: boolean, disabled: boolean) => ReactNode;
  renderUploadedActions?: (storedFileId: string, onRemove?: () => void) => ReactNode;
  showDefaultIcon?: boolean;
}

export const GenericDocumentUploader: FC<GenericDocumentUploaderProps> = ({
  documentName,
  fileType,
  storedFileId,
  onUpload,
  onRemove,
  disabled = false,
  acceptedFormats = '.pdf,.jpg,.jpeg,.png,.doc,.docx',
  className = '',
  uploadLabel,
  uploadedLabel = 'Transmis',
  renderUploadButton,
  renderUploadedActions,
  showDefaultIcon = true,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [isDragOver, setIsDragOver] = useState(false);

  // Override temporaire de l'état uploaded/removed.
  // Permet de refléter immédiatement l'action de l'utilisateur
  // sans attendre que la prop storedFileId soit mise à jour par le parent.
  // Réinitialisé automatiquement quand storedFileId change (la vraie donnée arrive).
  const [stateOverride, setStateOverride] = useState<'uploaded' | 'removed' | null>(null);

  useEffect(() => {
    setStateOverride(null);
  }, [storedFileId]);

  const inputId = `file-upload-${fileType}-${documentName.replace(/\s/g, '-')}`;
  const isUploaded = stateOverride === 'uploaded' || (stateOverride !== 'removed' && !!storedFileId);
  const { openDocumentViewer } = useDocumentViewerDialog();

  const handleFileUpload = useCallback(async (file: File) => {
    try {
      setIsLoading(true);
      setError(undefined);

      const result = await onUpload(file, fileType);

      if (!result.success) {
        setError(result.error || 'Erreur lors de l\'upload');
      } else {
        setStateOverride('uploaded');
      }
    } catch (err) {
      console.error(err);
      setError('Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  }, [fileType, onUpload]);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
    event.target.value = '';
  }, [handleFileUpload]);

  const handleRemove = useCallback(async () => {
    if (storedFileId && onRemove) {
      try {
        await onRemove(storedFileId);
        setStateOverride('removed');
      } catch (err) {
        console.error(err);
      }
    }
  }, [storedFileId, onRemove]);

  const handleClickUpload = useCallback(() => {
    document.getElementById(inputId)?.click();
  }, [inputId]);

  const handleViewDocument = useCallback(() => {
    if (storedFileId) {
      openDocumentViewer({
        title: documentName,
        storedFileId,
        showDefaultActions: true
      });
    }
  }, [storedFileId, documentName, openDocumentViewer]);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled && !isLoading && !isUploaded) {
      setIsDragOver(true);
    }
  }, [disabled, isLoading, isUploaded]);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    if (disabled || isLoading || isUploaded) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      // Validate file type
      const acceptedTypes = acceptedFormats.split(',').map(t => t.trim().toLowerCase());
      const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
      const isValidType = acceptedTypes.some(type =>
        type === fileExtension || file.type.includes(type.replace('.', ''))
      );

      if (isValidType) {
        handleFileUpload(file);
      } else {
        setError(`Format non supporté. Formats acceptés: ${acceptedFormats}`);
      }
    }
  }, [disabled, isLoading, isUploaded, acceptedFormats, handleFileUpload]);

  const renderDefaultUploadButton = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center gap-2">
          <GenericLoading size={Sizes.sm} loading />
        </div>
      );
    }

    return (
      <Button
        color="primary"
        className="cursor-pointer"
        onClick={handleClickUpload}
        disabled={disabled}
      >
        <UploadIcon className="w-4 h-4 mr-1" />
        {uploadLabel}
      </Button>
    );
  };

  const renderDefaultUploadedActions = () => (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-1.5 text-green-600">
        <CheckIcon className="w-4 h-4" />
        <span className="text-xs">{uploadedLabel}</span>
      </div>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          plain
          onClick={handleViewDocument}
          title="Visualiser le document"
        >
          <Eye className="w-4 h-4 text-gray-500 hover:text-primary" />
        </Button>
        {onRemove && (
          <Button
            type="button"
            plain
            onClick={handleRemove}
            disabled={disabled}
            title="Supprimer le document"
          >
            <TrashIcon className="w-4 h-4 text-gray-500 hover:text-red-500" />
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div
      key={documentName}
      className={`
        border rounded-lg p-4 transition-all duration-200
        ${isDragOver
          ? 'border-primary border-dashed bg-primary/5 border-2'
          : 'border-gray-200 bg-white'
        }
        ${!isUploaded && !disabled ? 'cursor-pointer' : ''}
        ${className}
      `}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={!isUploaded && !disabled && !isLoading ? handleClickUpload : undefined}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {showDefaultIcon && <DocumentIcon className="w-5 h-5 text-gray-500" />}
          <div>
            {isUploaded ? (
              <p
                className="font-medium text-sm hover:text-primary hover:underline cursor-pointer transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewDocument();
                }}
              >
                {documentName}
              </p>
            ) : (
              <p className="font-medium text-sm">{documentName}</p>
            )}
            {!isUploaded && (
              <p className="text-xs text-gray-400 mt-0.5">
                {isDragOver ? 'Déposez le fichier ici' : 'Cliquez ou glissez-déposez'}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          {isUploaded ? (
            renderUploadedActions && storedFileId
              ? renderUploadedActions(storedFileId, onRemove ? handleRemove : undefined)
              : renderDefaultUploadedActions()
          ) : (
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <input
                type="file"
                id={inputId}
                className="hidden"
                accept={acceptedFormats}
                onChange={handleFileChange}
                disabled={disabled || isLoading}
              />

              {renderUploadButton
                ? renderUploadButton(handleClickUpload, isLoading, disabled)
                : renderDefaultUploadButton()
              }
            </div>
          )}
        </div>
      </div>

      {error && <ErrorMessage>{error}</ErrorMessage>}
    </div>
  );
};
